import {
  Currency,
  ImpactShopProductType,
  ImpactType,
  impactTypeToProjectTypeMapper,
} from "@ecologi/shared";
import React from "react";
import { useRouter } from "next/router";
import { animateScroll as scroller } from "react-scroll";
import { Button } from "../Button";
import { ImpactShopProductCard } from "../ImpactShopProductCard/ImpactShopProductCard";
import styled from "@emotion/styled";
import { CARD_WIDTH } from "../ImpactShopProductCard/ImpactShopProductCard.styles";
import { ImpactShopFilterBar } from "./ImpactShopFilterBar";
import { useImpactShopFilter } from "./useImpactShopFilter";

const BottomSectionContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 32px 0 48px;
`;

const ImpactShopContentContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 16px;
  padding: 0 16px;
  background-color: ${({ theme }) => theme.colors.mist};

  @media ${({ theme }) => theme.bp.tabletUp} {
    grid-template-columns: repeat(auto-fill, minmax(${CARD_WIDTH}px, 1fr));
  }

  @media ${({ theme }) => theme.bp.wideDesktopUp} {
    grid-gap: 24px;
    padding: 0 24px;
  }
`;

const handleBackToTop = () => {
  scroller.scrollToTop();
};

export type ImpactShopHomeContentProps = {
  currency: Currency;
  products: ImpactShopProductType[];
};

export const ImpactShopHomeContent = ({
  currency: BASKET_CURRENCY,
  products,
}: ImpactShopHomeContentProps) => {
  const router = useRouter();
  const { impactType, setImpactType } = useImpactShopFilter();

  const handleCardClick = (slug: string) => () => {
    router.push(`/impact-shop/${slug}`);
  };

  return (
    <>
      <ImpactShopFilterBar
        impactType={impactType}
        setImpactType={setImpactType}
      />
      <ImpactShopContentContainer>
        {products.map((product, i) => {
          const price: number | undefined = product.prices.find(
            (price) => price.currency == BASKET_CURRENCY
          )?.amount;
          if (!price) return null;

          if (shouldFilterProductByImpactType(product, impactType)) {
            return null;
          }

          return (
            <ImpactShopProductCard
              badgePath={product.display.badgePath}
              currency={BASKET_CURRENCY}
              description={product.display.snippet || ""}
              image={product.display.images[0]}
              isOutOfStock={product.isOutOfStock}
              key={i}
              onCardClick={handleCardClick(product.slug)}
              price={price}
              tags={product.display.tags}
              title={product.display.title}
              unit={product.display.unitType}
              minQuantity={product.minQuantity}
              productType={product.productType}
            />
          );
        })}
      </ImpactShopContentContainer>
      <BottomSectionContainer>
        <Button
          border
          onClick={handleBackToTop}
          text={"Back to top"}
          textOnly
        />
      </BottomSectionContainer>
    </>
  );
};

const shouldFilterProductByImpactType = (
  product: ImpactShopProductType,
  impactTypeFilter: ImpactType | null
) => {
  if (!impactTypeFilter) {
    return false;
  }

  if (
    product.display.solutions?.some((solution) =>
      impactTypeToProjectTypeMapper[impactTypeFilter].includes(solution)
    )
  ) {
    return false;
  }

  return true;
};
